import React, { useState, useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import LogoPreloader from "./components/LogoPreloader";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { initAnalytics } from "./utils/analytics";
import { initPerformanceOptimizations } from "./utils/performanceOptimizations";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy load pages for better performance
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const LiftOverview = lazy(() => import("./pages/LiftOverview"));
const LiftDetail = lazy(() => import("./pages/LiftDetail"));
const BrandDetail = lazy(() => import("./pages/BrandDetail"));
const GeneratorDetail = lazy(() => import("./pages/GeneratorDetail"));
const GeneratorBrandDetail = lazy(() => import("./pages/GeneratorBrandDetail"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Media = lazy(() => import("./pages/Media"));
const Certificates = lazy(() => import("./pages/Certificates"));
const Contact = lazy(() => import("./pages/Contact"));
const CustomersPage = lazy(() => import("./pages/Customers"));

// Admin pages
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Categories = lazy(() => import("./pages/admin/Categories"));
const Brands = lazy(() => import("./pages/admin/Brands"));
const AdminProducts = lazy(() => import("./pages/admin/ProductsManagement"));
const AdminProjects = lazy(() => import("./pages/admin/ProjectsManagement"));
const AdminMedia = lazy(() => import("./pages/admin/MediaManagement"));
const AdminCertificates = lazy(() => import("./pages/admin/CertificatesManagement"));
const Customers = lazy(() => import("./pages/admin/CustomersManagement"));
const PDFManagement = lazy(() => import("./pages/admin/PDFManagement"));
const QuoteRequests = lazy(() => import("./pages/admin/QuoteRequests"));
const CMSHome = lazy(() => import("./pages/admin/CMSHome"));
const CMSAbout = lazy(() => import("./pages/admin/CMSAbout"));
const CMSContact = lazy(() => import("./pages/admin/CMSContact"));
const CMSPages = lazy(() => import("./pages/admin/CMSPages"));
const TimelineManagement = lazy(() => import("./pages/admin/TimelineManagement"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));

// Component to handle scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);

  return null;
}

function App() {
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  useEffect(() => {
    // Initialize performance optimizations
    initPerformanceOptimizations();
    
    // Initialize analytics
    initAnalytics();
    
    // Reduced initial load time to 1 second for better UX
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1000); // 1 second initial load time

    return () => clearTimeout(timer);
  }, []);

  // Show full screen loader for initial app load
  if (isInitialLoading) {
    return <LogoPreloader />;
  }

  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<LogoPreloader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<><Header /><Home /><Footer /></>} />
                <Route path="/about" element={<><Header /><About /><Footer /></>} />
                <Route path="/products" element={<><Header /><Products /><Footer /></>} />
                <Route path="/products/lift" element={<><Header /><LiftOverview /><Footer /></>} />
                <Route path="/products/lift/:type" element={<><Header /><LiftDetail /><Footer /></>} />
                <Route path="/products/lift/:type/brand/:brandName" element={<><Header /><BrandDetail /><Footer /></>} />
                <Route path="/products/lift/imported/:brand" element={<><Header /><LiftDetail /><Footer /></>} />
                <Route path="/products/generators" element={<><Header /><GeneratorDetail /><Footer /></>} />
                <Route path="/products/generators/brand/:brandName" element={<><Header /><GeneratorBrandDetail /><Footer /></>} />
                <Route path="/products/:productId" element={<><Header /><ProductDetail /><Footer /></>} />
                <Route path="/projects" element={<><Header /><Projects /><Footer /></>} />
                <Route path="/projects/:projectId" element={<><Header /><ProjectDetail /><Footer /></>} />
                <Route path="/media" element={<><Header /><Media /><Footer /></>} />
                <Route path="/certificates" element={<><Header /><Certificates /><Footer /></>} />
                <Route path="/customers" element={<><Header /><CustomersPage /><Footer /></>} />
                <Route path="/contact" element={<><Header /><Contact /><Footer /></>} />
                
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="categories" element={<Categories />} />
                  <Route path="brands" element={<Brands />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="projects" element={<AdminProjects />} />
                  <Route path="media" element={<AdminMedia />} />
                  <Route path="certificates" element={<AdminCertificates />} />
                  <Route path="customers" element={<Customers />} />
                  <Route path="pdfs" element={<PDFManagement />} />
                  <Route path="quotes" element={<QuoteRequests />} />
                  <Route path="cms-home" element={<CMSHome />} />
                  <Route path="cms-about" element={<CMSAbout />} />
                  <Route path="timeline" element={<TimelineManagement />} />
                  <Route path="cms-contact" element={<CMSContact />} />
                  <Route path="cms-pages" element={<CMSPages />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>
              </Routes>
            </Suspense>
            <Toaster />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;